import type { ComponentProps } from 'react';

import { cn } from '../cn';
import StepIconSvg, { StepIconSvgType } from './StepIconSvg';

export type StepIconState =
  | 'not-started'
  | 'skipped'
  | 'action-required'
  | 'responded'
  | 'finished'
  | 'interrupted'
  | 'canceled'
  | 'contact_unavailable';

type StepIconProps = ComponentProps<'div'> & {
  iconType: StepIconSvgType;
  /** Optional state for colored badge styling. Without state, renders plain icon. */
  state?: StepIconState;
};

export default function StepIcon({ iconType, state, className, ref, ...props }: StepIconProps) {
  // Without state, render simple icon wrapper
  if (state == null) {
    return (
      <div
        ref={ref}
        className={cn('block text-slate-500 dark:text-slate-400 [&>svg]:size-full', className)}
        aria-label={`${iconType}_icon`}
        {...props}
      >
        <StepIconSvg iconType={iconType} />
      </div>
    );
  }

  // With state, render colored badge
  return (
    <div
      ref={ref}
      className={cn(
        'flex size-5 shrink-0 items-center justify-center rounded-sm border [&>svg]:size-3 [&>svg]:stroke-2',
        {
          'border-slate-200 bg-slate-100 text-slate-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100':
            state === 'not-started',
          'border-slate-400 bg-slate-200 text-slate-800 opacity-30 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200':
            state === 'skipped',
          'border-quarter-spanish-white-500 bg-quarter-spanish-white-500 text-quarter-spanish-white-100':
            state === 'action-required',
          'border-mantis-500 bg-mantis-500 text-mantis-100': state === 'responded',
          'border-mantis-400 bg-mantis-100 text-mantis-600 dark:border-mantis-700 dark:bg-mantis-900 dark:text-mantis-50':
            state === 'finished',
          'border-fair-pink-500 bg-fair-pink-500 text-fair-pink-100 dark:border-fair-pink-900 dark:bg-fair-pink-800 dark:text-fair-pink-50':
            state === 'interrupted',
          'border-slate-600 bg-slate-700 text-slate-400 opacity-60': state === 'canceled',
          'border-red-400 bg-red-100 text-red-600 dark:border-red-700 dark:bg-red-900 dark:text-red-50':
            state === 'contact_unavailable',
        },
        className,
      )}
      {...props}
    >
      <StepIconSvg iconType={iconType} />
    </div>
  );
}